import { useEffect, useState } from "react";
import clsx from "clsx";
import { Button } from "../primitives";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { type Appearance, type Palette, useDisplaySettings } from "@/hooks/useDisplaySettings";
import { formatRelative, formatTimestamp } from "@/lib/time";

interface HeaderProps {
  environment?: string;
  mockMode?: boolean;
  generatedAt?: string;
}

export function Header({ environment = "production", mockMode = false, generatedAt }: HeaderProps) {
  const { paused, setPaused, lastRefreshIso, triggerNow } = useAutoRefresh();
  const display = useDisplaySettings();
  const [displayOpen, setDisplayOpen] = useState(false);
  // Force re-render every 5s so "x seconds ago" stays accurate.
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force((n) => n + 1), 5_000);
    return () => window.clearInterval(id);
  }, []);
  const lastIso = generatedAt ?? lastRefreshIso;

  return (
    <header className="sticky top-0 z-30 border-b border-border bg-bg/85 backdrop-blur">
      <div className="flex h-14 items-center gap-4 px-5">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-accent/15 text-accent">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2L4 6v6c0 5 3.5 9.3 8 10 4.5-.7 8-5 8-10V6l-8-4z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div>
            <div className="text-sm font-semibold text-fg leading-tight">Riskwatch</div>
            <div className="text-[10px] uppercase tracking-wider text-fg-subtle">Risk Score Dashboard</div>
          </div>
        </div>

        <span className={clsx(
          "rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider",
          environment === "production"
            ? "border-sev-good/40 bg-sev-good/10 text-sev-good"
            : "border-sev-watch/40 bg-sev-watch/10 text-sev-watch",
        )}>{environment}</span>

        {mockMode && (
          <span className="rounded-full border border-sev-suspicious/40 bg-sev-suspicious/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sev-suspicious">
            Mock data
          </span>
        )}

        <div className="ml-auto flex items-center gap-3">
          <div className="hidden text-right text-xs text-fg-muted md:block" title={formatTimestamp(lastIso)}>
            <div>Last updated</div>
            <div className="text-fg">{formatRelative(lastIso)}</div>
          </div>
          <Button variant="ghost" size="sm" onClick={triggerNow} title="Refresh now">
            <RefreshIcon />
          </Button>
          <Button
            variant={paused ? "primary" : "ghost"}
            size="sm"
            onClick={() => setPaused(!paused)}
            title={paused ? "Resume live updates" : "Pause live updates"}
          >
            {paused ? <PlayIcon /> : <PauseIcon />}
            <span className="hidden sm:inline">{paused ? "Resume" : "Pause"}</span>
          </Button>
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDisplayOpen((value) => !value)}
              title={`Display: ${display.appearance === "system" ? `System (${display.resolvedAppearance})` : labelAppearance(display.appearance)}, ${labelPalette(display.palette)}`}
              aria-haspopup="dialog"
              aria-expanded={displayOpen}
            >
              <DisplayIcon />
              <span className="hidden lg:inline">Display</span>
            </Button>
            {displayOpen && (
              <DisplaySettingsPopover
                appearance={display.appearance}
                palette={display.palette}
                resolvedAppearance={display.resolvedAppearance}
                setAppearance={display.setAppearance}
                setPalette={display.setPalette}
                onClose={() => setDisplayOpen(false)}
              />
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

function DisplaySettingsPopover({
  appearance,
  palette,
  resolvedAppearance,
  setAppearance,
  setPalette,
  onClose,
}: {
  appearance: Appearance;
  palette: Palette;
  resolvedAppearance: "light" | "dark";
  setAppearance: (appearance: Appearance) => void;
  setPalette: (palette: Palette) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label="Display settings"
      className="absolute right-0 top-full z-50 mt-2 w-[min(22rem,calc(100vw-1rem))] rounded-lg border border-border bg-surface-1 p-3 text-xs shadow-pop"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">Display settings</div>
          <div className="mt-0.5 text-fg-muted">Currently {resolvedAppearance} with {labelPalette(palette).toLowerCase()}</div>
        </div>
        <button type="button" onClick={onClose} className="text-fg-subtle hover:text-fg" aria-label="Close display settings">X</button>
      </div>

      <DisplaySegmentedControl
        label="Appearance"
        value={appearance}
        options={[
          ["system", "System"],
          ["light", "Light"],
          ["dark", "Dark"],
        ]}
        onChange={setAppearance}
      />
      <DisplaySegmentedControl
        label="Palette"
        value={palette}
        options={[
          ["standard", "Standard"],
          ["color-vision", "Color vision support"],
        ]}
        onChange={setPalette}
        className="mt-3"
      />
    </div>
  );
}

function DisplaySegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: T;
  options: [T, string][];
  onChange: (value: T) => void;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="flex flex-wrap gap-1 rounded-md border border-border bg-surface-2 p-1">
        {options.map(([option, optionLabel]) => (
          <button
            key={option}
            type="button"
            onClick={() => onChange(option)}
            className={clsx(
              "rounded px-2 py-1 text-xs font-medium transition-colors",
              value === option
                ? "bg-accent/20 text-fg ring-1 ring-accent/40"
                : "text-fg-muted hover:bg-surface-3 hover:text-fg",
            )}
          >{optionLabel}</button>
        ))}
      </div>
    </div>
  );
}

function labelAppearance(appearance: Appearance) {
  return appearance === "system" ? "System" : appearance === "light" ? "Light" : "Dark";
}

function labelPalette(palette: Palette) {
  return palette === "color-vision" ? "Color vision support" : "Standard";
}

function RefreshIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v6h6M20 20v-6h-6" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 10A8 8 0 006.34 6.34M4 14a8 8 0 0013.66 3.66" />
    </svg>
  );
}
function PauseIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>;
}
function PlayIcon() {
  return <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor"><path d="M7 4l13 8-13 8V4z" /></svg>;
}
function DisplayIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16v10H4z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 19h8M12 15v4" />
    </svg>
  );
}
